<template>
    <div
        :style="{ 'background-color': player.backColor, 'font-family': player.fontFamily }"
        class="pb-16 lg:pb-0"
    >
        <PlayerHeader v-if="player.showHeader" />
        <div
            class="w-full flex flex-col lg:flex-row px-4 py-6 sm:px-12 sm:py-12 space-y-6 lg:space-y-0 lg:space-x-6 items-start"
        >
            <PlayerMain @toggleFinish="toggleFinish" />
            <PlayerSidebar class="hidden lg:block rounded-xl lg:w-96" />
        </div>
        <PlayerFooter v-if="player.showFooter" />
    </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex";
import PlayerHeader from "./PlayerHeader.vue";
import PlayerMain from "./PlayerMain.vue";
import PlayerSidebar from "./PlayerSidebar.vue";
import PlayerFooter from "./PlayerFooter.vue";
import { sendWebhook } from "@/shared/utils";
import { localStorageService, STORAGE_KEY } from "@/shared/connection/local-storage.service";
import { playerStatsService } from "@/apps/player/services/player-stats.service";

export default {
    name: "PlayerBody",
    components: {
        PlayerHeader,
        PlayerMain,
        PlayerSidebar,
        PlayerFooter,
    },
    data() {
        return {
            KEY_FINISH: "LESSON.FINISH",
            KEY_DISPLAY: "PLAYER.DISPLAY",
        };
    },
    computed: {
        ...mapState("shared/view", ["preview"]),
        ...mapState("player/view", ["player", "watched"]),
        ...mapGetters("player/view", ["isWatched", "noHeadersFiles", "currentFile"]),
        currentIndex() {
            return this.player.files.indexOf(this.currentFile);
        },
        nextFile() {
            const currentIndex = this.noHeadersFiles.indexOf(this.currentFile);
            if (currentIndex < this.noHeadersFiles.length - 1) {
                return this.noHeadersFiles[currentIndex + 1];
            }
            return { id: null };
        },
    },
    async mounted() {
        await this.$nextTick(); // this fix load on Arc somehow [EAS-2261]
        const fileId = this.$route.query?.file;
        if (fileId) {
            this.setFileFromQuery(fileId);
        } else {
            this.setCurrentFile({ file: this.noHeadersFiles[0] || {} });
        }

        window._EC_PRODUCT_ID = this.player.ecProductIds[0] || null;
        window._EC_USER_ID = localStorageService.readValue(STORAGE_KEY.userId);

        this.checkForSavedInfo();

        this.displayPlayer();

        document.addEventListener("finishLesson", this.toggleFinish.bind(this));
    },
    methods: {
        ...mapMutations("player/view", [
            "setCurrentFile",
            "setWatched",
            "addWatched",
            "removeWatched",
        ]),
        checkForSavedInfo() {
            if (this.preview) return;

            if (window._EC_PRODUCT_ID && window._EC_USER_ID) {
                const script = document.createElement("script");
                script.src = "/js/pages/easyjson.js";
                script.onload = () => {
                    //maybe they are already here available
                    if (window.easyJSON) {
                        this.checking();
                        // if not wait for data from easyJSON
                    } else {
                        document.addEventListener("easyJsonReady", this.checking.bind(this));
                    }
                };
                document.head.appendChild(script);
            } else {
                this.checking();
            }
        },
        checking() {
            const watched = this.readLsAndEj(this.KEY_FINISH) || [];
            this.setWatched(watched);
        },
        setFileFromQuery(id) {
            this.setCurrentFile({ file: { id: Number(id) }, updateUrl: false });
        },
        toggleFinish() {
            if (this.isWatched) {
                this.unfinishFile();
            } else {
                this.finishFile(true, true);
            }
        },
        finishFile(goToNext, saveInfo = false) {
            if (this.player.webhookUrl && this.player.sendWebhooks?.finish && !this.preview) {
                sendWebhook(
                    {
                        type: "finishFile",
                        location: window.location.href,
                        ecUserId: window._EC_USER_ID,
                        ecProductId: window._EC_PRODUCT_ID,
                        fileName: this.currentFile.name,
                    },
                    this.player.webhookUrl
                );
            }

            this.markCompleted();
            this.addWatched(this.currentFile.id);

            if (saveInfo && !this.preview) {
                this.saveLsAndEj(this.KEY_FINISH, this.watched);
            }

            if (goToNext) {
                if (this.nextFile.id) {
                    this.setCurrentFile({ file: this.nextFile });
                }
            }
        },
        unfinishFile() {
            if (this.player.webhookUrl && this.player.sendWebhooks?.unfinish && !this.preview) {
                sendWebhook(
                    {
                        type: "unfinishFile",
                        location: window.location.href,
                        ecUserId: window._EC_USER_ID,
                        ecProductId: window._EC_PRODUCT_ID,
                        fileName: this.currentFile.name,
                    },
                    this.player.webhookUrl
                );
            }

            this.markCompleted(false);
            this.removeWatched(this.currentFile.id);

            if (!this.preview) {
                this.saveLsAndEj(this.KEY_FINISH, this.watched);
            }
        },
        saveLsAndEj(key, value) {
            const obj = {};
            obj[key] = JSON.stringify(value);
            localStorage.setItem(key, obj[key]);
            if (typeof easy_json !== "undefined") {
                // eslint-disable-next-line no-undef
                easy_json.patch(obj);
            }
        },
        readLsAndEj(key) {
            if (window.easyJSON && window.easyJSON[key]) {
                const value = window.easyJSON[key];
                //update localStorage in case they are different
                localStorage.setItem(key, value);
                return JSON.parse(value);
            } else {
                return JSON.parse(localStorage.getItem(key));
            }
        },
        displayPlayer() {
            if (this.preview) {
                return;
            }
            if (sessionStorage.getItem(this.KEY_DISPLAY)) {
                return;
            } else {
                sessionStorage.setItem(this.KEY_DISPLAY, true);
                if (this.player.webhookUrl && this.player.sendWebhooks.display) {
                    sendWebhook(
                        {
                            type: "displayPlayer",
                            location: window.location.href,
                            ecUserId: window._EC_USER_ID,
                            ecProductId: window._EC_PRODUCT_ID,
                            fileName: this.currentFile.name,
                        },
                        this.player.webhookUrl
                    );
                }
            }
        },
        markCompleted(done = true) {
            if (this.player.ecProductIds.length && !this.preview) {
                playerStatsService.markLessonCompletion(
                    this.player.uuid,
                    this.currentFile.id,
                    done
                );
            }
        },
    },
};
</script>
